import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../components/auth/AuthProvider';
import UserInfoForm from '../components/auth/UserInfoForm';
import LoginNavbar from '../components/auth/LoginNavbar';

export default function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<any>(null);

  // Get the intended destination from location state, default to /
  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // If user is not authenticated, redirect to login
      navigate('/login', { replace: true });
      return;
    }

    if (!isLoading && isAuthenticated) {
      // Get current Supabase user
      const getCurrentUser = async () => {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          setSupabaseUser(user);

          // Check if user needs to complete profile - check ALL required fields
          const displayName =
            user.user_metadata?.full_name ||
            user.user_metadata?.name ||
            user.user_metadata?.display_name;
          const country = user.user_metadata?.country;
          const howFoundUs = user.user_metadata?.how_found_us;
          const userType = user.user_metadata?.user_type;
          const companyName = user.user_metadata?.company_name;
          const roleDescription = user.user_metadata?.role_description;
          const companySegment = user.user_metadata?.company_segment;

          const hasDisplayName = Boolean(displayName && displayName.trim());
          const hasCountry = Boolean(country && country.trim());
          const hasHowFoundUs = Boolean(howFoundUs && howFoundUs.trim());
          const hasUserType = Boolean(userType && userType.trim());
          const hasRoleDescription = Boolean(roleDescription && roleDescription.trim());
          const hasCompanySegment = userType !== 'business' || Boolean(companySegment);

          // For business users, company name is also required
          const hasCompanyName =
            userType !== 'business' || Boolean(companyName && companyName.trim());

          const profileComplete =
            hasDisplayName &&
            hasCountry &&
            hasHowFoundUs &&
            hasUserType &&
            hasCompanyName &&
            hasCompanySegment &&
            hasRoleDescription;

          if (profileComplete) {
            // Profile is already complete, redirect to intended destination
            navigate(from, { replace: true });
          }
        }
      };

      getCurrentUser();
    }
  }, [isLoading, isAuthenticated, navigate, from]);

  const handleComplete = async () => {
    // Profile completed successfully, redirect to intended destination
    const justSignedUp = localStorage.getItem('justSignedUp') === 'true';
    if (justSignedUp) {
      localStorage.removeItem('justSignedUp');
      navigate('/account?tab=subscription', { replace: true });
    } else {
      navigate(from, { replace: true });
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    // Clear error after 5 seconds
    setTimeout(() => setError(null), 5000);
  };

  // Show loading while auth state is being determined
  if (isLoading || !supabaseUser) {
    return (
      <div className="min-h-screen bg-theme-light-page-bg dark:bg-theme-dark-page-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E34290]"></div>
      </div>
    );
  }

  // Don't render anything if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const initialDisplayName =
    supabaseUser.user_metadata?.full_name ||
    supabaseUser.user_metadata?.name ||
    supabaseUser.user_metadata?.display_name ||
    '';

  return (
    <>
      <LoginNavbar />
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 max-w-sm">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      <UserInfoForm
        initialDisplayName={initialDisplayName}
        onComplete={handleComplete}
        onError={handleError}
      />
    </>
  );
}
